import { useMemo, useState } from 'react';

import { curatorErrorCopy, statusLabelOf, statusToneOf, type CuratorChild } from '../../api/curator-model';
import { useCuratorGroupMemberAdd, useCuratorGroupMemberRemove, useCuratorGroups } from '../../api/curator-groups';
import { Badge, Button, Drawer, Field, Select } from '../../components/ui';
import { groupAssignOptions, groupMembershipChange } from './cabinet-model';

/**
 * The OFFLINE roster's row drawer (T.Z62, contract v1.9.6) — what a click on a child
 * of «Офлайн (назначено)» opens.
 *
 * The roster is a READ-ONLY projection by design (Р7/Р15: the rows ARE applications,
 * the ЕПГУ/УНТИ funnel owns every field), so the drawer's only editable control is
 * the «Группа» select: every child here is matched by definition — the row IS the
 * application — so the #26.12 handle (`applicationId`, v1.9.5) is always present and
 * the gate the prefill drawer needs (unmatched ⇒ nothing to assign) never fires. The
 * write goes straight over #26.12/#26.13 through the SAME hooks the «Группы» section
 * uses, so the api's own 400/409 (full group, wrong venue, already a member) stay the
 * authority and the hooks' cabinet-root invalidation refetches the roster, the group
 * cards and the working table together.
 *
 * The identifiers are the point of T.Z65: the ЕПГУ id and the УНТИ id are shown for
 * every matched child, right where the curator looks — ids are not contacts
 * (ОВ#5/A12/D9 covers phone/e-mail only, and no contact key exists on the type).
 */
export function OfflineChildDrawer({
  venueId,
  venueName,
  child,
  onClose,
  onSaved,
}: {
  /** The venue the roster is scoped to (#26.1 takes one venue) — the group list's scope. */
  venueId: number;
  venueName: string;
  child: CuratorChild;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const groups = useCuratorGroups(venueId);
  const groupOptions = useMemo(() => groupAssignOptions(groups.data ?? []), [groups.data]);
  const [groupDraft, setGroupDraft] = useState<string>(() => (child.group === null ? '' : String(child.group.id)));
  const [error, setError] = useState<string | null>(null);
  const addMember = useCuratorGroupMemberAdd();
  const removeMember = useCuratorGroupMemberRemove();
  const busy = addMember.isPending || removeMember.isPending;

  // The membership write the draft asks for, folded once (`cabinet-model`): a move is
  // BOTH calls — remove from the old group, then assign the new one.
  const change = groupMembershipChange(child.group, groupDraft === '' ? null : Number(groupDraft));

  const save = () => {
    setError(null);
    if (change === null) {
      onSaved('Изменений нет.');
      return;
    }
    const applicationId = child.applicationId;
    if (applicationId === null) {
      // Defensive: #26.1's projection requires the ЕПГУ key, so every roster row has
      // one — but the honest refusal beats a request the api would answer 400 to.
      setError('У этого ребёнка нет номера заявления — назначить группу нельзя.');
      return;
    }
    if (change.removeFrom !== undefined) {
      removeMember.mutate(
        { groupId: change.removeFrom, applicationId },
        {
          onSuccess: () => {
            if (change.assignTo === undefined) {
              onSaved('Группа убрана.');
              return;
            }
            addMember.mutate(
              { groupId: change.assignTo, applicationId },
              {
                onSuccess: (group) => onSaved(`Группа: «${group.name}».`),
                onError: (cause) => setError(curatorErrorCopy(cause)),
              },
            );
          },
          onError: (cause) => setError(curatorErrorCopy(cause)),
        },
      );
      return;
    }
    if (change.assignTo !== undefined) {
      addMember.mutate(
        { groupId: change.assignTo, applicationId },
        {
          onSuccess: (group) => onSaved(`Группа: «${group.name}».`),
          onError: (cause) => setError(curatorErrorCopy(cause)),
        },
      );
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        // T.Z139 — an unnamed child opens the same drawer: the title falls back to
        // the number the api keys the row by.
        child.fullName ?? `Номер заявления ${child.pguId}`
      }
      description={`${venueName} · запись офлайн-списка: только чтение, править можно учебную группу.`}
      footer={
        <>
          <Button variant="ghost" data-testid="offline-child-cancel" disabled={busy} onClick={onClose}>
            {change === null ? 'Закрыть' : 'Отмена'}
          </Button>
          <Button
            variant="primary"
            data-testid="offline-child-save"
            loading={busy}
            disabled={change === null || groups.isPending}
            onClick={save}
          >
            Сохранить
          </Button>
        </>
      }
    >
      {/* T.Z65 — the identifiers of a MATCHED child, in the drawer as in the table. */}
      <dl className="cabinet-child-facts" data-testid="offline-child-facts">
        <div className="cabinet-child-facts__row">
          <dt>Номер заявления (ЕПГУ)</dt>
          <dd data-testid="offline-child-pgu">{child.pguId === '' ? '—' : child.pguId}</dd>
        </div>
        <div className="cabinet-child-facts__row">
          <dt>УНТИ ID</dt>
          <dd data-testid="offline-child-unti">{child.untiId ?? '—'}</dd>
        </div>
        <div className="cabinet-child-facts__row">
          <dt>Регион</dt>
          <dd>{child.region ?? '—'}</dd>
        </div>
        <div className="cabinet-child-facts__row">
          <dt>Программа</dt>
          <dd>{child.programName ?? '—'}</dd>
        </div>
        <div className="cabinet-child-facts__row">
          <dt>Статус</dt>
          <dd>
            <Badge tone={statusToneOf(child.currentStatus)} title={statusLabelOf(child.currentStatus)}>
              {statusLabelOf(child.currentStatus)}
            </Badge>
          </dd>
        </div>
      </dl>

      <Field label="Группа" htmlFor="offline-child-group" hint="Учебные группы этой площадки — до 30 учеников в каждой.">
        <Select
          id="offline-child-group"
          data-testid="offline-child-group"
          value={groupDraft}
          disabled={busy || groups.isPending}
          onChange={(event) => setGroupDraft(event.target.value)}
        >
          {groupOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>

      {error !== null ? (
        <p className="field__error" role="alert" data-testid="offline-child-error">
          {error}
        </p>
      ) : null}
      <p className="cabinet-note" data-testid="offline-child-note">
        Остальные поля ведут ЕПГУ и УНТИ — их в кабинете не правят.
      </p>
    </Drawer>
  );
}
